// Perspective maths for the wall visualiser. The wall is drawn flat and then
// mapped onto the four marked corners of the photo, exactly as _turn_onto()
// does in api/visualiser.py.

// Gaussian elimination with partial pivoting, for the 8x8 perspective system.
function solve(matrix, values) {
  const n = values.length;
  const rows = matrix.map((row, index) => [...row, values[index]]);
  for (let col = 0; col < n; col += 1) {
    let pivot = col;
    for (let r = col + 1; r < n; r += 1) if (Math.abs(rows[r][col]) > Math.abs(rows[pivot][col])) pivot = r;
    [rows[col], rows[pivot]] = [rows[pivot], rows[col]];
    for (let r = 0; r < n; r += 1) {
      if (r !== col && rows[r][col]) {
        const factor = rows[r][col] / rows[col][col];
        rows[r] = rows[r].map((value, index) => value - factor * rows[col][index]);
      }
    }
  }
  return rows.map((row, index) => row[n] / row[index]);
}

// The 3x3 map taking each `from` point to the matching `to` point.
export function homography(from, to) {
  const matrix = [];
  const values = [];
  from.forEach(([x, y], index) => {
    const [u, v] = to[index];
    matrix.push([x, y, 1, 0, 0, 0, -x * u, -y * u]);
    matrix.push([0, 0, 0, x, y, 1, -x * v, -y * v]);
    values.push(u, v);
  });
  const [a, b, c, d, e, f, g, h] = solve(matrix, values);
  return [a, b, c, d, e, f, g, h];
}

export function apply([a, b, c, d, e, f, g, h], [x, y]) {
  const w = g * x + h * y + 1;
  return [(a * x + b * y + c) / w, (d * x + e * y + f) / w];
}

// For an element with transform-origin 0 0.
export const matrix3d = ([a, b, c, d, e, f, g, h]) => `matrix3d(${[a, d, 0, g, b, e, 0, h, 0, 0, 1, 0, c, f, 0, 1].join(",")})`;

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

// Same test as quad_is_usable(): convex, not twisted and not tiny.
export function quadIsUsable(points) {
  const signs = new Set(points.map((point, i) => Math.sign(cross(point, points[(i + 1) % 4], points[(i + 2) % 4])) || 1));
  let area = 0;
  points.forEach(([x, y], i) => {
    const [nx, ny] = points[(i + 1) % 4];
    area += x * ny - nx * y;
  });
  return signs.size === 1 && Math.abs(area) / 2 > 0.002;
}

export const DEFAULT_CORNERS = [
  [0.2, 0.18],
  [0.8, 0.18],
  [0.8, 0.78],
  [0.2, 0.78],
];
